"use client";

import Link from "next/link";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import ImageCarousel, { type CarouselImage } from "@/components/home/ImageCarousel";

// Add/remove entries here to change what shows in the membership carousel.
const MEMBERSHIP_IMAGES: CarouselImage[] = [
  { src: "/img/teretana_unutra.webp", alt: "Saiyan Gym — teretana s žutim akcentima i fitness opremom" },
  { src: "/img/saiyan10.webp", alt: "Saiyan Gym — Nautilus sprave za trening snage s Goku muralom u pozadini" },
  { src: "/img/saiyan9.webp", alt: "Saiyan Gym — red crnih sprava i klupa za trening snage sa stripovnim muralom" },
];

type Membership = {
  title: string;
  price?: string;
  period?: string;
  highlighted?: boolean;
  showCta?: boolean;
  benefits?: string[];
  description?: string;
};

export default function MembershipOptionsSection() {
  const t = useTranslations("MembershipOptions");
  const memberships = t.raw("memberships") as Membership[];

  return (
    <section id="clanarine" className="mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10">

      {/* Heading */}
      <h2 className="mb-8 text-4xl font-black leading-tight tracking-tight text-[#18181b] sm:text-5xl">
        {t("heading")}
      </h2>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-8">

        {/* Left — membership cards */}
        <div className="flex flex-col gap-3 lg:w-[46%] lg:flex-shrink-0">
          {memberships.map((m, i) =>
            m.description !== undefined ? (
              /* Simplified card — no price, benefits, or CTA */
              <div
                key={i}
                className="flex flex-col items-start gap-1 rounded-2xl border border-[#E4E4E7] bg-[#F9FAFB] p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <p className="text-sm font-semibold text-[#18181b]">{m.title}</p>
                <p className="text-sm font-medium text-[#71717a]">{m.description}</p>
              </div>
            ) : (
              <div
                key={i}
                className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-start sm:gap-6 ${
                  m.highlighted
                    ? "border-[#EAB308]/60 bg-[#FEFCE8]"
                    : "border-[#E4E4E7] bg-[#F9FAFB]"
                }`}
              >
                {/* Left part of card — title, price, CTA */}
                <div className="flex flex-1 flex-col gap-2">
                  <div>
                    <p className="text-sm font-semibold text-[#18181b]">{m.title}</p>
                    <p className="text-lg font-black text-[#18181b]">
                      {m.price}{" "}
                      <span className={`text-xs font-semibold ${m.highlighted ? "text-[#CA8A04]" : "text-[#71717a]"}`}>
                        / {m.period}
                      </span>
                    </p>
                  </div>
                  {m.showCta && (
                    <Link
                      href="/register"
                      className={`inline-block w-fit rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                        m.highlighted
                          ? "bg-[#18181b] text-white hover:bg-[#27272a]"
                          : "border border-[#E4E4E7] bg-white text-[#18181b] hover:bg-[#F4F4F5]"
                      }`}
                    >
                      {t("ctaStart")}
                    </Link>
                  )}
                </div>

                {/* Right part of card — benefits */}
                <ul className="flex flex-1 flex-col gap-1.5">
                  {m.benefits!.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2 text-xs text-[#3f3f46]">
                      <Check className="h-3.5 w-3.5 flex-shrink-0 text-[#18181b]" strokeWidth={2.5} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>

        {/* Right — gym image */}
        <div className="w-full lg:flex-1">
          <ImageCarousel images={MEMBERSHIP_IMAGES} className="aspect-[4/3] lg:aspect-auto lg:h-full" />
        </div>

      </div>
    </section>
  );
}
